"use client";

import React, { useState, useRef } from "react";
import FormFieldWrapper from "../formFieldWrapper";
import SearchProducts from "../searchProducts";
import { useAppSelector } from "@/store/hooks";
import {
  MinimumPurchaseType,
  DiscountType,
  ApplicableProductsTypeType,
} from "@/types/coupon/discount";
import { useDiscountUpdate } from "@/hooks/useDiscountUpdate";
import SelectedProductsList from "../selectedProductsList";
import { InputPrimary } from "@/components/common/InputPrimary";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import SelectedCollectionsList from "../SelectedCollectionsList";
import { toast } from "sonner";

const currentPage = "buyXGetY";

const SelectProducts = () => {
  const {
    applicable_products_type,
    applicable_x_products,
    applicable_y_products,
    applicable_x_collections,
    applicable_y_collections,
    minimum_purchase_type,
    minimum_purchase_value_amount,
    minimum_purchase_value_quantity,
    discount_type,
    discount_value_percentage,
    discount_value_fixed,
    discount_value_max,
    max_uses_per_order,
  } = useAppSelector((state) => state.discount[currentPage]);

  const [
    applicable_products_type_for_get_y,
    set_applicable_products_type_for_get_y,
  ] = useState<ApplicableProductsTypeType>("specific");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleUpdate = useDiscountUpdate();

  const [isMaxUsageLimit, setIsMaxUsageLimit] = useState(false);

  const handleDiscountFieldChange = ({
    field,
    value,
  }: {
    field: string;
    value: string;
  }) => {
    if (parseFloat(value) < 0) {
      toast.error("Negative numbers are not supported.");
    } else if (
      field === "discount_value_percentage" &&
      parseFloat(value) > 100
    ) {
      toast.error("Percentage cannot exceed 100%.");
      handleUpdate(currentPage, { [field]: "100" });
      return;
    } else {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    handleUpdate(currentPage, { [field]: value });
  };

  const handleAppliesToDropdownChange = (value: ApplicableProductsTypeType) => {
    handleUpdate(currentPage, { applicable_products_type: value });
    if (value === "specific") {
      handleUpdate(currentPage, { applicable_x_collections: [] });
    } else if (value === "specificCollections") {
      handleUpdate(currentPage, { applicable_x_products: [] });
    }
  };

  const handleAppliesToDropdownChangeForGetY = (
    value: ApplicableProductsTypeType,
  ) => {
    set_applicable_products_type_for_get_y(value);
    if (value === "specific") {
      handleUpdate(currentPage, { applicable_y_collections: [] });
    } else if (value === "specificCollections") {
      handleUpdate(currentPage, { applicable_y_products: [] });
    }
  };

  const handleInputChange = ({
    field,
    value,
  }: {
    field:
      | "minimum_purchase_value_amount"
      | "minimum_purchase_value_quantity"
      | "get_y_quantity"
      | "discount_value_percentage"
      | "discount_value_fixed"
      | "discount_value_max"
      | "max_uses_per_order";
    value: string;
  }) => {
    if (parseFloat(value) < 0) {
      setErrors((prev) => ({
        ...prev,
        [field]: "Negative numbers are not supported.",
      }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    handleUpdate(currentPage, { [field]: value });
  };

  const handlePurchaseTypeSelectionChange = (value: MinimumPurchaseType) => {
    handleUpdate(currentPage, { minimum_purchase_type: value });
  };

  const handleDiscountTypeChange = (value: DiscountType) => {
    handleUpdate(currentPage, { discount_type: value });
  };

  const handleRemoveCollection = (key: string, id: string) => {
    const currentCollections =
      key === "applicable_x_collections"
        ? applicable_x_collections
        : applicable_y_collections;
    handleUpdate(currentPage, {
      [key]: currentCollections.filter((c) => c.id !== id),
    });
  };

  const handleRemoveProduct = (key: string, id: string) => {
    const currentProducts =
      key === "applicable_x_products"
        ? applicable_x_products
        : applicable_y_products;
    handleUpdate(currentPage, {
      [key]: currentProducts.filter((p) => p.id !== id),
    });
  };

  const minimumPurchaseTypeOptions = [
    { value: "quantity", label: "Minimum quantity of items" },
    { value: "amount", label: "Minimum purchase amount" },
  ];

  const discountTypeOptions = [
    { value: "percentage", label: "Percentage" },
    { value: "fixed", label: "Amount off each" },
    { value: "free", label: "Free" },
  ];

  const searchProductsModalRef = useRef<() => void>(undefined);

  return (
    <FormFieldWrapper>
      <RadioGroup
        className="flex space-x-5 pt-3"
        value={minimum_purchase_type}
        onValueChange={handlePurchaseTypeSelectionChange}
      >
        {minimumPurchaseTypeOptions.map(({ label, value }) => (
          <div key={value} className="flex items-center space-x-2">
            <RadioGroupItem value={value} id={value} />
            <Label htmlFor={value}>{label}</Label>
          </div>
        ))}
      </RadioGroup>
      <h5>Customer Buys</h5>
      <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(min(250px,100%),1fr))] gap-5">
        <div>
          <h6 className="text-[13px] text-muted-foreground font-normal mb-1 pl-1">
            {minimum_purchase_type === "quantity" ? "Quantity" : "Amount"}
          </h6>
          <InputPrimary
            onChange={(e) => {
              handleInputChange({
                field:
                  minimum_purchase_type === "quantity"
                    ? "minimum_purchase_value_quantity"
                    : "minimum_purchase_value_amount",
                value: e.target.value,
              });
            }}
            placeholder={minimum_purchase_type === "quantity" ? "0" : "₹ 0.00"}
            value={
              minimum_purchase_type === "quantity"
                ? minimum_purchase_value_quantity
                : minimum_purchase_value_amount
            }
            className="bg-transparent border rounded-xl h-[50px]"
          />
        </div>
        <div className="flex items-center space-x-8 mt-5">
          <label className="flex items-center space-x-3">
            <input
              type="radio"
              name="appliesTo"
              value="specific"
              checked={applicable_products_type === "specific"}
              onChange={() => handleAppliesToDropdownChange("specific")}
              className="form-radio h-5 w-5 text-primary focus:ring focus:ring-offset-2 focus:ring-primary"
            />
            <span className="text-[14px] font-medium">Specific Products</span>
          </label>
          <label className="flex items-center space-x-3">
            <input
              type="radio"
              name="appliesTo"
              value="specificCollections"
              checked={applicable_products_type === "specificCollections"}
              onChange={() =>
                handleAppliesToDropdownChange("specificCollections")
              }
              className="form-radio h-5 w-5 text-primary focus:ring focus:ring-offset-2 focus:ring-primary"
            />
            <span className="text-[14px] font-medium">
              Specific Collections
            </span>
          </label>
        </div>
      </div>

      <SearchProducts
        applicableProductsType={applicable_products_type}
        currentPage="buyXGetY"
        applicableProductsKey="applicable_x_products"
        applicableCollectionsKey="applicable_x_collections"
        openInSelectedTabRef={searchProductsModalRef}
      />
      {applicable_products_type === "specificCollections" && (
        <SelectedCollectionsList
          currentPage={currentPage}
          collectionsList={applicable_x_collections}
          onRemove={(id: string) =>
            handleRemoveCollection("applicable_x_collections", id)
          }
          onEdit={() => searchProductsModalRef.current?.()}
        />
      )}
      <SelectedProductsList
        currentPage={currentPage}
        productsList={applicable_x_products}
        onRemove={(id: string) =>
          handleRemoveProduct("applicable_x_products", id)
        }
      />
      <h5>Customer Gets</h5>
      <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(min(250px,100%),1fr))] gap-5">
        <div>
          <h6 className="text-[13px] text-muted-foreground font-normal mb-1 pl-1">
            Quantity
          </h6>
          <InputPrimary
            onChange={(e) => {
              handleInputChange({
                field: "get_y_quantity",
                value: e.target.value,
              });
            }}
            type="number"
            className="bg-transparent border rounded-xl h-[50px]"
          />
          {errors.get_y_quantity && (
            <p className="text-red-500 text-xs mt-1">{errors.get_y_quantity}</p>
          )}
        </div>
        <div className="flex items-center space-x-8 mt-5">
          <label className="flex items-center space-x-3">
            <input
              type="radio"
              name="appliesToGetY"
              value="specific"
              checked={applicable_products_type_for_get_y === "specific"}
              onChange={() => handleAppliesToDropdownChangeForGetY("specific")}
              className="form-radio h-5 w-5 text-primary focus:ring focus:ring-offset-2 focus:ring-primary"
            />
            <span className="text-[14px] font-medium">Specific Products</span>
          </label>
          <label className="flex items-center space-x-3">
            <input
              type="radio"
              name="appliesToGetY"
              value="specificCollections"
              checked={
                applicable_products_type_for_get_y === "specificCollections"
              }
              onChange={() =>
                handleAppliesToDropdownChangeForGetY("specificCollections")
              }
              className="form-radio h-5 w-5 text-primary focus:ring focus:ring-offset-2 focus:ring-primary"
            />
            <span className="text-[14px] font-medium">
              Specific Collections
            </span>
          </label>
        </div>
      </div>

      <SearchProducts
        applicableProductsType={applicable_products_type_for_get_y}
        currentPage="buyXGetY"
        applicableProductsKey="applicable_y_products"
        applicableCollectionsKey="applicable_y_collections"
      />

      {applicable_products_type === "specificCollections" && (
        <SelectedCollectionsList
          currentPage={currentPage}
          collectionsList={applicable_y_collections}
          onRemove={(id: string) =>
            handleRemoveCollection("applicable_y_collections", id)
          }
        />
      )}
      <SelectedProductsList
        currentPage={currentPage}
        productsList={applicable_y_products}
        onRemove={(id: string) =>
          handleRemoveProduct("applicable_y_products", id)
        }
      />
      <div className="border rounded-md p-4">
        <h5>At a discounted value</h5>
        <RadioGroup
          className="flex space-x-4"
          value={discount_type}
          onValueChange={handleDiscountTypeChange}
        >
          {discountTypeOptions.map(({ label, value }) => (
            <div key={value} className="flex items-center space-x-2">
              <RadioGroupItem value={value} id={value} />
              <Label htmlFor={value}>{label}</Label>
            </div>
          ))}
        </RadioGroup>
        {discount_type !== "free" && (
          <div>
            <h6 className="text-[13px] text-muted-foreground font-normal mb-1 pl-1">
              {discount_type === "percentage"
                ? "Percentage"
                : "Amount off each"}
            </h6>
            <InputPrimary
              onChange={(e) =>
                handleDiscountFieldChange({
                  field:
                    discount_type === "percentage"
                      ? "discount_value_percentage"
                      : "discount_value_fixed",
                  value: e.target.value,
                })
              }
              placeholder="0.00"
              value={
                discount_type === "percentage"
                  ? discount_value_percentage
                  : discount_value_fixed
              }
              type="number"
              className="bg-transparent border rounded-xl h-[50px]"
              onKeyDown={(e) => {
                if (
                  e.key === "-" ||
                  e.key === "e" ||
                  e.key === "E" ||
                  e.key === "+"
                ) {
                  e.preventDefault();
                }
              }}
            />
            {(errors.discount_value_percentage ||
              errors.discount_value_fixed) && (
              <p className="text-red-500 text-xs mt-1">
                {errors.discount_value_percentage ||
                  errors.discount_value_fixed}
              </p>
            )}
            {discount_type === "percentage" && (
              <div className="mt-4">
                <h6 className="text-[13px] text-muted-foreground font-normal mb-1 pl-1">
                  Maximum discount value
                </h6>
                <InputPrimary
                  onChange={(e) =>
                    handleDiscountFieldChange({
                      field: "discount_value_max",
                      value: e.target.value,
                    })
                  }
                  placeholder="0.00"
                  value={discount_value_max}
                  type="number"
                  className="bg-transparent border rounded-xl h-[50px]"
                />
                {errors.discount_value_max && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.discount_value_max}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex justify-between items-center">
        <Label htmlFor="max-usage-limit">
          Set a maximum number of uses per order
        </Label>
        <Switch
          id="max-usage-limit"
          checked={isMaxUsageLimit}
          onCheckedChange={() => setIsMaxUsageLimit((prev) => !prev)}
        />
      </div>
      {isMaxUsageLimit && (
        <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(min(250px,100%),1fr))] gap-5">
          <InputPrimary
            onChange={(e) =>
              handleDiscountFieldChange({
                field: "max_uses_per_order",
                value: e.target.value,
              })
            }
            type="number"
            className="bg-transparent border rounded-xl h-[50px]"
            placeholder="Enter max usage limit"
            value={max_uses_per_order}
          />
          {errors.discount_value_max && (
            <p className="text-red-500 text-xs mt-1">
              {errors.discount_value_max}
            </p>
          )}
        </div>
      )}
    </FormFieldWrapper>
  );
};

export default SelectProducts;
